import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { updatePaymentLinks } from '../../actions/payment-links';
import Select from 'react-select';
import {
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormFeedback,
  Col,
  Row,
  CustomInput
} from 'reactstrap';
import DatePicker from '../date-time-picker';
import { GQL_PAYMENTLINK_CREATE_BY_SLUG, GQL_PAYMENTLINK_UPDATE_BY_SLUG } from '../../gqls';
import {
  validator,
  paymentLinkUrl,
  getUserTimezone,
  sanitizeDOM,
  REGEX_SLUG,
  slug,
  paymentTypeOptionsWebinar
} from '../../utils';
import Icon from '../icon';
import moment from 'moment-timezone';
import SubmitButton from '../submit-button';
import RichEditor, { getPlainText, getContentString, getEditorContent } from '../rich-text-editor';
import { EditorState } from 'draft-js';
import { CustomStyles, CustomStylesInvalid } from '../custom-styles/react-select-styles';
import MultipleImageVideoInput from '../multiple-image-video-input';
import MayarAlert from '../alert/alert';

const ModalBodyWebinar = ({ isOpen = false, onClose, onSuccess, data }) => {
  if (!isOpen) return null;

  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only

  const today = new Date();
  const tomorrow = new Date(today);
  const [paymentType, setPaymentType] = useState(data?.paymentType || 'paid');
  const [insertPaymentLink, { data: newpl, loading: submitCreateLoading }] = useMutation(
    GQL_PAYMENTLINK_CREATE_BY_SLUG
  );
  const [editPaymentLink, { loading: submitUpdateLoading }] = useMutation(GQL_PAYMENTLINK_UPDATE_BY_SLUG);
  const { register, handleSubmit, errors, control } = useForm();
  const [multipleImageId, setMultipleImageId] = useState('');

  const [submitError, setSubmitError] = useState(null);

  const [date, setDate] = useState(data ? data.expiredAt : null);
  const [eventStartAt, setEventStartAt] = useState(data ? data.eventStartAt : null);
  const [eventEndAt, setEventEndAt] = useState(data ? data.eventEndAt : null);
  const [link, setLink] = useState(data ? data.link : '');
  const dispatch = useDispatch();
  const isUpdate = !data ? false : true;
  const stateEdit = EditorState.createEmpty();
  const [editorState, setEditorState] = useState(stateEdit);
  const [description, setDescription] = useState('');
  const [textDescription, setTextDescription] = useState('');
  const [check, setCheck] = useState(true);
  const [invalidDescription, setInvalidDescription] = useState(false);
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);

  if (!data) data = {};

  const setEditor = content => {
    setEditorState(content);
    setDescription(getContentString(content));
    setTextDescription(getPlainText(content));
  };

  useEffect(() => {
    if (textDescription.length <= 0 && !check) {
      setInvalidDescription(true);
    } else {
      setInvalidDescription(false);
    }
  }, [textDescription]);

  useEffect(() => {
    if (isUpdate && data.description) {
      // Preprocess description if it starts with img tag to prevent broken page caused by Draft JS behaviour
      let processedDescription = data.description;
      if (processedDescription.startsWith('<img')) {
        processedDescription = `<p></p>${processedDescription}`;
      }

      const content = getEditorContent(processedDescription);
      setEditor(content);
    }
  }, [data]);

  const onSubmit = async values => {
    if (textDescription.length <= 0) {
      setCheck(false);
      setInvalidDescription(true);
      return;
    }

    if (date !== null) setDate(date);
    if (eventStartAt !== null) setEventStartAt(eventStartAt);
    if (eventEndAt !== null) setEventEndAt(eventEndAt);
    if (!values.limit) {
      delete values.limit;
    } else {
      values.limit = parseInt(values.limit);
    }
    if (values.paymentType === 'free') {
      values.amount = 0;
    } else if (values.paymentType === 'voluntary' && !values.amount) {
      values.amount = 0;
    } else {
      values.amount = parseFloat(values.amount);
    }

    if (values.crossoutPrice) values.crossoutPrice = parseFloat(values.crossoutPrice);
    else values.crossoutPrice = null;

    if (values.affiliateCommission) values.affiliateCommission = parseFloat(values.affiliateCommission);
    else values.affiliateCommission = null;

    try {
      if (isUpdate) {
        values.id = data.id;
        let statusUpdate = 'closed';
        if (!date || new Date(date) > Date.now()) {
          statusUpdate = 'active';
        }
        const objeditPaymentLinkBySlug = {
          ...values,
          expiredAt: date ? new Date(date).setHours(0, 0, 0, 0) : eventStartAt,
          eventStartAt: eventStartAt,
          eventEndAt: eventEndAt,
          status: statusUpdate,
          description: sanitizeDOM(description),
          ...(deletedCoverContents.length && {
            deletedCoverContents: deletedCoverContents.map(item => {
              if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                return {
                  videoMultipleFileId: item?.id
                };
              }
              return {
                imageId: item.editImage?.id || item.id
              };
            })
          }),
          ...(multipleCoverContent.length && {
            orderedMultipleCoverContent: multipleCoverContent.map(item => {
              if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                return {
                  position: Number(item.position),
                  videoId: item.videoId || item.file?.id || item.value,
                  editId: item?.id
                };
              }
              return {
                position: Number(item.position),
                imageId: item.editImage?.id || item.id
              };
            })
          })
        };

        if (multipleImageId) {
          objeditPaymentLinkBySlug.multipleImageId = multipleImageId;
        }
        await editPaymentLink({
          variables: {
            input: objeditPaymentLinkBySlug
          }
        });
        dispatch(updatePaymentLinks(Math.random()));
      } else {
        const objPaymentLink = {
          ...values,
          expiredAt: date ? new Date(date).setHours(0, 0, 0, 0) : eventStartAt,
          link: slug(values.name),
          type: 'webinar',
          status: 'active',
          eventStartAt: eventStartAt,
          eventEndAt: eventEndAt,
          description: sanitizeDOM(description),
          ...(deletedCoverContents.length && {
            deletedCoverContents: deletedCoverContents.map(item => {
              if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                return {
                  videoMultipleFileId: item?.id
                };
              }
              return {
                imageId: item.editImage?.id || item.id
              };
            })
          }),
          ...(isolatedMultipleCoverContent.length && {
            orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
              if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                return {
                  position: Number(item.position),
                  videoId: item.videoId || item.file?.id || item.value,
                  editId: item?.id
                };
              }
              return {
                position: Number(item.position),
                imageId: item.editImage?.id || item.id
              };
            })
          })
        };

        if (multipleImageId) {
          objPaymentLink.multipleImageId = multipleImageId;
        }
        const paymentLink = await insertPaymentLink({
          variables: {
            input: objPaymentLink
          }
        });
        if (paymentLink?.data?.insertPaymentLinkBySlug.errorMessage) {
          throw new Error(paymentLink?.data?.insertPaymentLinkBySlug.errorMessage || 'Terjadi kesalahan saat membuat produk');
        }
        //segment tracking
        window.analytics.track('Create Webinar', values);
      }
    } catch (error) {
      setSubmitError(error.message || "Error! Silahkan coba lagi atau hubungi CS");
      window.analytics.track('Create Webinar - ERROR', values);
    }
  };

  if (newpl && newpl.insertPaymentLinkBySlug && !newpl.insertPaymentLinkBySlug.errorMessage) {
    onSuccess(newpl.insertPaymentLinkBySlug.id);
  }
  return (
    <Modal isOpen={isOpen} fade size="lg">
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Webinar</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>
      <ModalBody>
        <p>
          <small>Mengadakan webinar, menerima pendaftaran dan pembayaran semakin mudah dengan Mayar</small>
        </p>
        <hr />
        {submitError && <MayarAlert isError message={submitError} />}

        <Form onSubmit={handleSubmit(onSubmit)} id="create-payment-link">
          <FormGroup>
            <Label for="name">Judul Webinar*</Label>
            <Input
              type="text"
              name="name"
              id="name"
              invalid={errors.name}
              defaultValue={data.name}
              innerRef={register({
                required: validator.required,
                minLength: validator.minLength(5)
              })}
            />
            <FormFeedback>{errors.name?.message}</FormFeedback>
          </FormGroup>
          {isUpdate && (
            <FormGroup>
              <Label for="name">Slug*</Label>
              <Input
                type="text"
                name="link"
                id="link"
                invalid={errors.link}
                defaultValue={data.link}
                onChange={e => setLink(e.target.value)}
                innerRef={register({
                  required: validator.required,
                  pattern: validator.pattern(REGEX_SLUG)
                })}
              />
              <FormFeedback>{errors.link?.message}</FormFeedback>
              <small>{'contoh : ' + paymentLinkUrl(link)}</small>
            </FormGroup>
          )}

          <FormGroup>
            <Label for="paymentType">Tipe Pembayaran</Label>
            <Controller
              name="paymentType"
              control={control}
              defaultValue={paymentType}
              render={({ onChange, name, value: defaultPaymentType }) => (
                <Select
                  id={name}
                  name={name}
                  options={paymentTypeOptionsWebinar}
                  defaultValue={paymentTypeOptionsWebinar.find(obj => obj.value === defaultPaymentType)}
                  placeholder="Pilih Tipe Pembayaran"
                  styles={!!errors.paymentType ? CustomStylesInvalid : CustomStyles}
                  className={`${!!errors.paymentType ? 'is-invalid' : ''}`}
                  onChange={({ value }) => {
                    setPaymentType(value);
                    onChange(value);
                  }}
                />
              )}
            />
          </FormGroup>

          {paymentType === 'paid' && (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="amount">Harga*</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="amount"
                      id="amount"
                      invalid={errors.amount}
                      defaultValue={data.amount}
                      innerRef={register({
                        required: validator.required,
                        min: validator.min(1000),
                        max: validator.max(100000000)
                      })}
                    />
                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                  </InputGroup>
                  <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="crossoutPrice">Harga Coret (opsional)</Label>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>Rp</InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="number"
                      name="crossoutPrice"
                      id="crossoutPrice"
                      defaultValue={data.crossoutPrice}
                      invalid={errors.crossoutPrice}
                      innerRef={register({
                        min: validator.min(1000),
                        max: validator.max(100000000)
                      })}
                    />
                    <FormFeedback>{errors.crossoutPrice?.message}</FormFeedback>
                  </InputGroup>
                  <small>Harga coret harus lebih besar dari harga utama.</small>
                </FormGroup>
              </Col>
            </Row>
          )}

          {paymentType === 'voluntary' && (
            <FormGroup>
              <Label for="amount">Minimal Harga</Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>Rp</InputGroupText>
                </InputGroupAddon>
                <Input
                  type="number"
                  name="amount"
                  id="amount"
                  invalid={errors.amount}
                  placeholder="0+"
                  defaultValue={data.amount}
                  innerRef={register({
                    // required: validator.required,
                    min: validator.min(0),
                    max: validator.max(100000000)
                  })}
                />
                <FormFeedback>{errors.amount?.message}</FormFeedback>
              </InputGroup>
              <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
            </FormGroup>
          )}

          <FormGroup>
            <Label for="description">Deskripsi*</Label>
            <RichEditor onEditorStateChange={setEditor} editorState={editorState} invalidState={invalidDescription} />
            <FormFeedback className={`${invalidDescription ? 'd-block' : ''}`}>Wajib diisi</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="eventUrl">
              Webinar URL/Link (Zoom/Google Meet/Youtube Live, dll)*{' '}
              <img src="/webinar-platform.png" alt="webinar platform" height="20" />
            </Label>
            <Input
              type="text"
              name="eventUrl"
              id="eventUrl"
              placeholder="https://us04web.zoom.us/j/XXXXXX?pwd=XXXXX"
              invalid={errors.eventUrl}
              defaultValue={data.eventUrl}
              innerRef={register({ pattern: validator.url, required: validator.required })}
            />
            <FormFeedback>{errors?.eventUrl?.message}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="eventStartAt">Waktu Mulai*</Label>
            <div className="mb-1 date-block">
              <DatePicker
                showTimeSelect
                placeholderText="Pilih Tanggal dan Jam"
                dateFormat="yyyy/MM/dd HH:mm"
                timeFormat="HH:mm"
                selected={eventStartAt ? new Date(eventStartAt) : null}
                minDate={new Date()}
                required
                className="rui-datetimepicker form-control"
                onChange={d => setEventStartAt(d)}
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Label for="eventEndAt">Waktu Selesai</Label>
            <div className="mb-1 date-block">
              <DatePicker
                showTimeSelect
                placeholderText="Pilih Tanggal dan Jam"
                dateFormat="yyyy/MM/dd HH:mm"
                timeFormat="HH:mm"
                selected={eventEndAt ? new Date(eventEndAt) : null}
                minDate={new Date()}
                className="rui-datetimepicker form-control"
                onChange={d => setEventEndAt(d)}
              />
            </div>
            <small>
              Timezone menggunakan {moment().tz(getUserTimezone()).format('z')} (GMT
              {moment().tz(getUserTimezone()).format('Z')}) - {moment.tz.guess()}
            </small>
          </FormGroup>

          <MultipleImageVideoInput
            productName={data.name}
            setMultipleImageId={setMultipleImageId}
            multipleImage={data.multipleImage}
            onLoading={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isAutoUploadOnSelectFile={false}
            maxVideo={1}
            maxImage={5}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />

          <FormGroup>
            <Label for="notes">Instruksi</Label>
            <Input
              type="textarea"
              name="notes"
              id="notes"
              invalid={errors.notes}
              defaultValue={data.notes}
              innerRef={register()}
            />
            <FormFeedback>{errors.notes?.message}</FormFeedback>
            <small>
              Instruksi adalah catatan yang akan dilihat oleh pendaftar setelah melakukan pendaftaran/membayar. Anda
              bisa memasukkan instruksi masuk ke zoom, passwordnya, formulir google, kontak cs dll disini
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="terms">Syarat dan Ketentuan</Label>
            <Input
              type="textarea"
              name="terms"
              id="terms"
              invalid={errors.terms}
              defaultValue={data.terms}
              innerRef={register()}
            />
            <FormFeedback>{errors.terms?.message}</FormFeedback>
          </FormGroup>
          <hr />
          <FormGroup>
            <Label for="startAt">Waktu Mulai Penjualan</Label>
            <div className="mb-1">
              <Controller
                name="startAt"
                control={control}
                defaultValue={data.startAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih Tanggal Mulai"
                    showTimeSelect
                    dateFormat="yyyy/MM/dd HH:mm"
                    timeFormat="HH:mm"
                    selected={value ? new Date(value) : null}
                    minDate={new Date()}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.startAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.startAt?.message}</FormFeedback>
            <small>
              Kami akan membuka link pembayaran pada tanggal dan waktu yang anda pilih. Opsional, kosongkan untuk
              langsung membuka penjualan
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="kadaluarsa4">Tanggal Pendaftaran Ditutup</Label>
            <div className="mb-1">
              <DatePicker
                placeholder="Pilih tanggal atau kosongkan"
                dateFormat="yyyy/MM/dd"
                selected={date ? new Date(date) : null}
                minDate={tomorrow.setDate(tomorrow.getDate() + 1)}
                className="rui-datetimepicker form-control w-auto"
                onChange={d => setDate(d)}
              />
            </div>
            <small>
              Kami akan menutup pendaftaran pada tanggal ini (opsional), jika tidak diisi kami akan menutup webinar ini
              setelah acara dimulai.
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="limit">Maksimum Jumlah Peserta (Kuota / Qty)</Label>
            <Input
              type="number"
              name="limit"
              id="limit"
              invalid={errors.limit}
              defaultValue={data.limit}
              innerRef={register()}
            />
            <FormFeedback>{errors?.limit?.message}</FormFeedback>
            <small>
              Kami akan menutup pendaftaran setelah melewati batas jumlah maksimal. Kosongkan untuk tanpa limit jumlah
              (unlimited)
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="redirectUrl">Redirect URL</Label>
            <Input
              type="text"
              name="redirectUrl"
              id="redirectUrl"
              placeholder="https://websiteanda.com/terimakasih"
              invalid={errors.redirectUrl}
              defaultValue={data.redirectUrl}
              innerRef={register({ pattern: validator.url })}
            />
            <FormFeedback>{errors?.redirectUrl?.message}</FormFeedback>
            <small>Pelanggan akan dibawa kehalaman ini setelah membayar (opsional /bisa dikosongkan)</small>
          </FormGroup>

          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>

                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}

          <SubmitButton
            size="lg"
            block
            color="brand"
            type="submit"
            className="text-center"
            loading
            isLoading={submitCreateLoading || submitUpdateLoading}
            isDisabled={disableSubmitButton}
            text={`${isUpdate ? 'Update' : 'Buat'} Webinar`}
          />
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalBodyWebinar;
